import React, { useState, useEffect } from 'react';
import ConfettiAnimation from '../shared/ConfettiAnimation';

interface CalendarDay {
  day: number;
  opened: boolean;
  content: string;
}

const AdventCalendar: React.FC = () => {
  const ADVENT_CALENDAR_DAYS = 24;
  const CONFETTI_DURATION = 3000;
  const DECEMBER_MONTH = 11;
  
  const [testMode, setTestMode] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });
  const [countdown, setCountdown] = useState('');
  const [days, setDays] = useState<CalendarDay[]>(() => 
    Array.from({ length: ADVENT_CALENDAR_DAYS }, (_, i) => ({
      day: i + 1,
      opened: false,
      content: `Überraschung für Tag ${i + 1}!`
    }))
  );

  const openDay = (dayNumber: number, event: React.MouseEvent) => {
    const today = new Date().getDate();
    const currentMonth = new Date().getMonth();
    const isDecember = currentMonth === DECEMBER_MONTH;
    
    // In test mode, allow opening any day. In production, only allow if it's December and the day has arrived
    if (!testMode && (!isDecember || dayNumber > today)) {
      return;
    }

    setDays(prev => prev.map(day => 
      day.day === dayNumber ? { ...day, opened: true } : day
    ));
    
    // Capture click position
    setClickPosition({ x: event.clientX, y: event.clientY });
    
    // Trigger confetti animation
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), CONFETTI_DURATION);
  };

  const today = new Date().getDate();
  const currentMonth = new Date().getMonth();
  const isDecember = currentMonth === DECEMBER_MONTH;

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    
    if (!isDecember && !testMode) {
      const MILLISECONDS_IN_SECOND = 1000;
      const SECONDS_IN_MINUTE = 60;
      const MINUTES_IN_HOUR = 60;
      const HOURS_IN_DAY = 24;
      const DECEMBER_MONTH = 11;
      
      timer = setInterval(() => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const nextDecember = new Date(currentYear + (now.getMonth() === DECEMBER_MONTH ? 1 : 0), DECEMBER_MONTH, 1);
        const diff = nextDecember.getTime() - now.getTime();
        
        const days = Math.floor(diff / (MILLISECONDS_IN_SECOND * SECONDS_IN_MINUTE * MINUTES_IN_HOUR * HOURS_IN_DAY));
        const hours = Math.floor((diff % (MILLISECONDS_IN_SECOND * SECONDS_IN_MINUTE * MINUTES_IN_HOUR * HOURS_IN_DAY)) / (MILLISECONDS_IN_SECOND * SECONDS_IN_MINUTE * MINUTES_IN_HOUR));
        const minutes = Math.floor((diff % (MILLISECONDS_IN_SECOND * SECONDS_IN_MINUTE * MINUTES_IN_HOUR)) / (MILLISECONDS_IN_SECOND * SECONDS_IN_MINUTE));
        const seconds = Math.floor((diff % (MILLISECONDS_IN_SECOND * SECONDS_IN_MINUTE)) / MILLISECONDS_IN_SECOND);
        
        setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      }, MILLISECONDS_IN_SECOND);
    }
    
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isDecember, testMode]);

  return (
    <div style={{ 
      padding: window.innerWidth < 768 ? '10px' : '20px', 
      textAlign: 'center', 
      position: 'relative',
      backgroundImage: 'url(/gameZoneImages/AdventCalendar/Advent_Background.jpg)',
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
      minHeight: '80vh',
      overflow: 'hidden'
    }}>

      <ConfettiAnimation show={showConfetti} position={clickPosition} />
      <div style={{ 
        marginBottom: '20px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '10px'
      }}>
        <span style={{ fontSize: '14px', color: '#191919' }}>✨ Demo Modus</span>
        <button
          onClick={() => setTestMode(!testMode)}
          style={{
            width: '50px',
            height: '30px',
            backgroundColor: testMode ? '#4d943a' : '#ccc',
            borderRadius: '15px',
            position: 'relative',
            cursor: 'pointer',
            transition: 'background-color 0.3s ease',
            border: 'none'
          }}
          aria-label={`Demo Modus ${testMode ? 'deaktivieren' : 'aktivieren'}`}
        >
          <div
            style={{
              width: '26px',
              height: '26px',
              backgroundColor: 'white',
              borderRadius: '50%',
              position: 'absolute',
              top: '2px',
              left: testMode ? '22px' : '2px',
              transition: 'left 0.3s ease',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          />
        </button>
      </div>
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '20px',
        borderRadius: '10px',
        position: 'relative'
      }}>
        {!isDecember && !testMode && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ color: '#8e8279', margin: 0 }}>
              Der Adventskalender ist nur im Dezember verfügbar!
            </p>
            <p style={{ color: '#191919', fontSize: '14px', margin: '5px 0 0 0' }}>
              ⏰ Nächster Start: {countdown}
            </p>
          </div>
        )}
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth < 768 ? 'repeat(4, 1fr)' : 'repeat(6, 1fr)',
          gap: '10px'
        }}>

        {days.map((day) => {
          const canOpen = testMode || (isDecember && day.day <= today);
          const isClickable = canOpen && !day.opened && !showConfetti;
          
          const getBackgroundColor = () => {
            if (day.opened) return '#4d943a';
            if (canOpen) return testMode ? '#BCB5A9' : '#191919';
            return '#ccc';
          };
          
          const getAriaLabel = () => {
            if (day.opened) return `Tag ${day.day} geöffnet`;
            if (canOpen) return `Tag ${day.day} öffnen`;
            return `Tag ${day.day} noch nicht verfügbar`;
          };
          
          return (
            <button
              key={day.day}
              onClick={(e) => isClickable && openDay(day.day, e)}
              disabled={!isClickable}
              style={{
                width: window.innerWidth < 768 ? '60px' : '80px',
                height: window.innerWidth < 768 ? '60px' : '80px',
                backgroundColor: getBackgroundColor(),
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                border: 'none',
                cursor: isClickable ? 'pointer' : 'default',
                fontSize: '18px',
                fontWeight: 'bold',
                transition: 'all 0.3s ease',
                opacity: canOpen ? 1 : 0.5
              }}
              aria-label={getAriaLabel()}
            >
              {day.opened ? '🎁' : day.day}
            </button>
          );
        })}
        </div>
      </div>

    </div>
  );
};

export default AdventCalendar;